import React, { useCallback, useState } from "react";
import { useTheme } from "@emotion/react";
import {
  Breakpoints,
  useBreakPoints,
} from "components/context/BreakPointContext";
import Backdrop from "components/backdrop/Backdrop";
import MenuItem from "components/sidebar/menuItem/MenuItem";
import {
  Ads,
  Chat,
  Diamond,
  Info,
  LogoutIcon,
  Notification,
  ProfileIcon,
  Question,
  Education,
  NewButtonVariant,
  NewButtonColor,
} from "@bale/ui";
import ProfileHeader from "./ProfileHeader/ProfileHeader";
import { strings } from "intl/fa";
import styles from "./Sidebar.module.scss";
import Modal from "components/commons/modal/Modal";
import VersionNumber from "./versionNumber/VersionNumber";
import { useNavigate } from "react-router-dom";
import { LocalStorageKeys, openLinkInNewPage } from "../../utils/utils";
import {
  AppPage,
  NavigationActions,
  SidebarOption,
} from "../../core/navigation/navigation.slice";
import { useAppDispatch, useAppSelector } from "../../utils/store";
import { Credit } from "./credit/Credit";
import { useIsVip } from "../../hooks/useFeatureScopes";
import CustomPackageNotif from "./customPackageNotif/CustomPackageNotif";
import classNames from "classnames";
import { PANEL_LINKS } from "utils/constants";
import { useMyPanel } from "hooks/useMyPanel";
import useLimitVisible from "hooks/useLimitVisible";
import { Calendar, Cash, MenuH } from "@bale/icons";
import PackagesInfo from "./PackagesInfo";

type MenuItemInfo = {
  title: string;
  value: SidebarOption;
  onClick: () => void;
  disabled?: boolean;
  isNew?: boolean;
  visibleData?: ReturnType<typeof useLimitVisible>;
};

const getIcon = (itemType: SidebarOption, isSelected: boolean) => {
  switch (itemType) {
    case SidebarOption.OrderAdvertisement:
      return Ads;
    case SidebarOption.Profile:
      return ProfileIcon;
    case SidebarOption.Notifications:
      return Notification;
    case SidebarOption.FAQ:
      return Question;
    case SidebarOption.Support:
      return Chat;
    case SidebarOption.TermsAndConditions:
      return Info;
    case SidebarOption.Council:
      return Diamond;
    case SidebarOption.Logout:
      return LogoutIcon;
    case SidebarOption.Tutorial:
      return Education;
    case SidebarOption.AdvertisementPrices:
      return Cash;
    case SidebarOption.Calendar:
      return Calendar;
  }
};

type Props = {
  closeSidebar?: React.Dispatch<React.SetStateAction<boolean>>;
};

const Sidebar = (props: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const myPanel = useMyPanel();
  const dispatch = useAppDispatch();
  const { customPackageInfo } = useAppSelector((state) => state.orderAd);
  const currentSidebarOption = useAppSelector(
    (state) => state.navigation.sidebar
  );
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const hasCustomPackage = customPackageInfo?.name;

  const mainMenuItems: MenuItemInfo[] = [
    {
      title: strings.side_bar.order,
      value: SidebarOption.OrderAdvertisement,
      onClick: () => {
        dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      },
    },
    // {
    //   title: strings.side_bar.calendar,
    //   value: SidebarOption.Calendar,
    //   onClick: () => {
    //     changeSidebarOption(SidebarOption.Calendar);
    //     dispatch(NavigationActions.setOrdersPage(AppPage.Calendar));
    //   },
    // },
    {
      title: strings.side_bar.ad_prices,
      value: SidebarOption.AdvertisementPrices,
      onClick: () => {
        dispatch(NavigationActions.setAppPage(AppPage.AdvertisementPrices));
      },
      isNew: true,
      visibleData: useLimitVisible({ id: "AdPricing", visibleFor: 3 }),
    },
    {
      title: strings.side_bar.support,
      value: SidebarOption.Support,
      onClick: () => openLinkInNewPage(PANEL_LINKS.SUPPORT_BOT),
    },
    {
      title: strings.side_bar.tutorial,
      value: SidebarOption.Tutorial,
      onClick: () => {
        dispatch(NavigationActions.setAppPage(AppPage.Tutorial));
      },
      isNew: true,
      visibleData: useLimitVisible({ id: "AdTutorial", visibleFor: 3 }),
    },
    {
      title: strings.side_bar.faq,
      value: SidebarOption.FAQ,
      onClick: () => openLinkInNewPage(PANEL_LINKS.FAQ),
    },
    {
      title: strings.side_bar.terms,
      value: SidebarOption.TermsAndConditions,
      onClick: () => openLinkInNewPage(PANEL_LINKS.TERMS),
    },
  ];

  const isVip = useIsVip();

  const logout = useCallback(() => {
    localStorage.removeItem(LocalStorageKeys.AccessToken);
    navigate("/login");
    dispatch(NavigationActions.resetState());
  }, []);

  const closeLogoutModal = useCallback(() => {
    setShowLogoutModal(false);
  }, []);

  return (
    <>
      <div
        onClick={(e) => e.stopPropagation()}
        className={styles.Container}
        dir="ltr" // Makes scroll bar be left while children are RTL, RTL is handled in the children components
      >
        <div dir="rtl" className={styles.InnerContainer}>
          <ProfileHeader closeSidebar={props.closeSidebar} />
          {isVip && (!hasCustomPackage || myPanel.credit > 0) && <Credit />}
          {isVip && hasCustomPackage && (
            <CustomPackageNotif packageInfo={customPackageInfo} />
          )}
          <div
            className={classNames(
              styles.MenuItemContainer
              // styles.BorderBottom
            )}
          >
            {mainMenuItems.map((item) => {
              const isSelected = currentSidebarOption === item.value;
              const Icon = getIcon(item.value, isSelected);
              return (
                <MenuItem
                  {...item}
                  isNew={item.isNew && item.visibleData?.shouldShow}
                  ref={item.visibleData?.elementRef}
                  selected={isSelected}
                  key={item.title}
                  icon={
                    <Icon
                      size={24}
                      className={styles.SidebarIcon}
                      color={theme.colors.dark[300]}
                    />
                  }
                />
              );
            })}
            <MenuItem
              title={strings.side_bar.logout}
              value={SidebarOption.Logout}
              icon={
                <LogoutIcon
                  size={24}
                  className={styles.SidebarIcon}
                  color={theme.colors.dark[300]}
                />
              }
              onClick={() => setShowLogoutModal(true)}
            />
          </div>
          {(!isVip || myPanel.credit === 0) && <PackagesInfo />}
          <div className={styles.versionNumber}>
            <VersionNumber />
          </div>
        </div>
      </div>
      {showLogoutModal && (
        <Modal
          title={strings.side_bar.logout}
          primaryButton={{
            onClick: logout,
            label: strings.logout.title,
            color: NewButtonColor.ERROR,
          }}
          onClose={closeLogoutModal}
          secondaryButton={{
            onClick: closeLogoutModal,
            label: strings.common.button.cancel,
            variant: NewButtonVariant.OUTLINED,
          }}
          content={strings.logout.description}
        />
      )}
    </>
  );
};

const ResponsiveSidebar: React.FC<React.PropsWithChildren<unknown>> = () => {
  const breakpoint = useBreakPoints();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  if (breakpoint <= Breakpoints.Small)
    return (
      <>
        <MenuH
          onClick={() => setIsMobileSidebarOpen(true)}
          className={styles.Hamburger}
          size={20}
        />
        <Backdrop
          isOpen={isMobileSidebarOpen}
          onClick={() => setIsMobileSidebarOpen(false)}
        >
          <Sidebar closeSidebar={setIsMobileSidebarOpen} />
        </Backdrop>
      </>
    );

  return <Sidebar />;
};

export default ResponsiveSidebar;
